import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Share2,
  Plus,
  Ban,
  Trash2,
  ExternalLink,
  RotateCcw,
} from 'lucide-react';
import { useSocialPosts, useDeletePost, useCancelPost, useRetryPost } from '@/hooks/useSocial';
import { Card, Button, PageHeader, EmptyState } from '@solid/ui';
import { Badge } from '@/components/ui/Badge';
import type { SocialPostStatus, PubPost, SocialNetwork } from '@/types';

const STATUS_TABS: Array<{ value: SocialPostStatus | ''; label: string }> = [
  { value: 'QUEUED', label: 'Queued' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'FAILED', label: 'Failed' },
];

const STATUS_BADGE: Record<
  SocialPostStatus,
  { variant: 'default' | 'primary' | 'success' | 'warning' | 'error'; label: string }
> = {
  QUEUED: { variant: 'default', label: 'Queued' },
  SCHEDULED: { variant: 'primary', label: 'Scheduled' },
  PUBLISHED: { variant: 'success', label: 'Published' },
  FAILED: { variant: 'error', label: 'Failed' },
  CANCELLED: { variant: 'warning', label: 'Cancelled' },
};

const NETWORK_LABELS: Record<SocialNetwork, string> = {
  FACEBOOK: 'FB',
  INSTAGRAM: 'IG',
  TWITTER: 'X',
  LINKEDIN: 'LI',
  PINTEREST: 'Pin',
  TIKTOK: 'TT',
  YOUTUBE: 'YT',
  GOOGLE_BUSINESS: 'GB',
  TELEGRAM: 'TG',
  WORDPRESS: 'WP',
  MASTODON: 'Mast',
  THREADS: 'Thr',
  BLUESKY: 'BS',
};

function NetworkBadge({ network, status }: { network: SocialNetwork; status: SocialPostStatus }) {
  const variant = STATUS_BADGE[status]?.variant || 'default';
  return (
    <Badge variant={variant} className="text-xs">
      {NETWORK_LABELS[network] || network}
    </Badge>
  );
}

export default function SocialPosts() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<SocialPostStatus | ''>('QUEUED');
  const deleteMutation = useDeletePost();
  const cancelMutation = useCancelPost();
  const retryMutation = useRetryPost();

  const { data, isLoading } = useSocialPosts({
    status: statusFilter || undefined,
  });

  const posts = data?.data || [];
  const total = data?.total || 0;

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (window.confirm('Delete this post? This cannot be undone.')) {
      deleteMutation.mutate(id);
    }
  };

  const handleCancel = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (window.confirm('Cancel this post?')) {
      cancelMutation.mutate(id);
    }
  };

  const handleRetry = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    retryMutation.mutate(id);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Social Posts"
        subtitle="Manage your social media posting queue"
        actions={
          <>
            <Button variant="ghost" onClick={() => navigate('/admin/social/templates')}>
              Templates
            </Button>
            <Button onClick={() => navigate('/admin/social/new')}>
              <Plus className="w-4 h-4 mr-1" />
              New Post
            </Button>
          </>
        }
      />

      {/* Status tabs */}
      <div className="flex gap-2 border-b border-grey-200">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setStatusFilter(tab.value)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              statusFilter === tab.value
                ? 'border-primary text-primary'
                : 'border-transparent text-grey-500 hover:text-secondary'
            }`}
          >
            {tab.label}
          </button>
        ))}
        <span className="ml-auto text-sm text-grey-400 self-center pr-2">
          {total} post{total !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Posts list */}
      {isLoading ? (
        <div className="text-center py-12 text-grey-400">Loading...</div>
      ) : posts.length === 0 ? (
        <Card padding="md">
          <EmptyState
            icon={<Share2 />}
            title="No posts yet"
            action={
              <Button variant="ghost" onClick={() => navigate('/admin/social/new')}>
                Create your first post
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              onDelete={handleDelete}
              onCancel={handleCancel}
              onRetry={handleRetry}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PostCard({
  post,
  onDelete,
  onCancel,
  onRetry,
}: {
  post: PubPost;
  onDelete: (e: React.MouseEvent, id: string) => void;
  onCancel: (e: React.MouseEvent, id: string) => void;
  onRetry: (e: React.MouseEvent, id: string) => void;
}) {
  const navigate = useNavigate();
  const content = post.content;
  const preview = content.default?.slice(0, 120) || '(no content)';
  const badge = STATUS_BADGE[post.status];

  return (
    <Card
      padding="sm"
      className="cursor-pointer hover:border-primary/30 transition-colors"
      onClick={() => {
        if (post.status === 'QUEUED') {
          navigate(`/admin/social/${post.id}/edit`);
        }
      }}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* Content preview */}
          <p className="text-body text-secondary truncate">{preview}</p>

          {/* Channel badges */}
          <div className="flex flex-wrap gap-1.5 mt-2">
            {post.postChannels.map((pc) => (
              <NetworkBadge
                key={pc.id}
                network={pc.channel?.network || ('FACEBOOK' as SocialNetwork)}
                status={pc.status}
              />
            ))}
          </div>

          {/* Fail reason */}
          {post.postChannels.some((pc) => pc.failReason) && (
            <p className="text-xs text-error mt-2 truncate">
              {post.postChannels.find((pc) => pc.failReason)?.failReason}
            </p>
          )}

          {/* Meta row */}
          <div className="flex items-center gap-3 mt-2 text-xs text-grey-400">
            <Badge variant={badge.variant}>{badge.label}</Badge>
            <span>{post.source === 'ai-agent' ? 'AI Agent' : 'Manual'}</span>
            {post.mediaUrls.length > 0 && (
              <span>{post.mediaUrls.length} media</span>
            )}
            <span>
              {new Date(post.createdAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          {post.status === 'PUBLISHED' && post.postChannels.some((pc) => pc.publerUrl) && (
            <a
              href={post.postChannels.find((pc) => pc.publerUrl)?.publerUrl!}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="p-1.5 rounded hover:bg-grey-100 text-grey-400 hover:text-primary"
              aria-label="Open published post"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
          {(post.status === 'FAILED' || post.status === 'CANCELLED') && (
            <button
              onClick={(e) => onRetry(e, post.id)}
              className="px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20 text-xs font-medium flex items-center gap-1"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Retry
            </button>
          )}
          {(post.status === 'QUEUED' || post.status === 'SCHEDULED') && (
            <button
              onClick={(e) => onCancel(e, post.id)}
              className="p-1.5 rounded hover:bg-grey-100 text-grey-400 hover:text-warning"
              aria-label="Cancel post"
            >
              <Ban className="w-4 h-4" />
            </button>
          )}
          {post.status === 'QUEUED' && (
            <button
              onClick={(e) => onDelete(e, post.id)}
              className="p-1.5 rounded hover:bg-grey-100 text-grey-400 hover:text-error"
              aria-label="Delete post"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}
